import { ATableHeader } from '../../organisms/ATable';

// --- Types ---

export interface BatchReportInfo {
    id?: string;
    identification?: string;
    provider?: string;
    createdAt?: string;
    updatedAt?: string;
    status?: string;
    documents?: number;
    errors?: number;
    success?: number;
    sending?: number;
    statusFormatted?: string;
    reportType?: string;
    batchReportId?: string;
    canUpdateStatus?: boolean;
    mini_uuid: string;
    reportTypeInfo?: {
        id?: string;
        name?: string;
        description?: string;
        provider?: string;
        documentType?: string;
    };
}

export interface BatchReportItem {
    document: string;
    createdAt?: string;
    provider?: string;
    status?: string;
    statusFormatted?: string;
    errorMessage?: string | null;
    [key: string]: any;
}

export interface ReportItem {
    document: string;
    documentRaw?: string;
    createdAt?: string;
    updatedAt?: string;
    status?: string;
    statusFormatted?: string;
    errorMessage?: string | null;
    reportType?: string;
    reportTypeId?: string;
    batchReportId?: string;
    attempts?: number;
    maxAttempts?: number;
    events?: Array<{
        eventId: string;
        eventName: string;
        occurredAt: string;
    }>;
}

// --- Status Configuration ---

export interface StatusConfig {
    label: string;
    color: string;
    bgClass: string;
    description: string;
}

export const STATUS_MAP: Record<string, StatusConfig> = {
    PENDING: { label: 'Pendente', color: '#EAB308', bgClass: 'bg-yellow-500', description: 'Aguardando inicio da consulta ao provedor' },
    PROCESSING: { label: 'Processando', color: '#6B7280', bgClass: 'bg-gray-500', description: 'Consulta ao provedor em progresso' },
    WAITING: { label: 'Aguardando', color: '#9CA3AF', bgClass: 'bg-gray-400', description: 'Aguardando retorno de dados do provedor' },
    ERROR: { label: 'Erro', color: '#EF4444', bgClass: 'bg-red-500', description: 'Erro na consulta ou extração de dados' },
    FAILED: { label: 'Falhou', color: '#991B1B', bgClass: 'bg-red-800', description: 'Atingido limite de erros na consulta' },
    COMPLETED: { label: 'Concluído', color: '#3B82F6', bgClass: 'bg-blue-500', description: 'Extração de dados finalizada' },
};

export const STATUS_OPTIONS = Object.entries(STATUS_MAP).map(([value, cfg]) => ({ value, label: cfg.label }));

// --- Constants ---

const EVENT_COLOR_MAP = new Map<string, string>([
    ['BATCH_REPORT_CREATED', 'bg-green-500'],
    ['BATCH_REPORT_PROCESSING_STARTED', 'bg-indigo-500'],
    ['BATCH_REPORT_DOCUMENT_PROCESSED', 'bg-blue-400'],
    ['BATCH_REPORT_DOCUMENT_FAILED', 'bg-red-400'],
    ['BATCH_REPORT_COMPLETED', 'bg-blue-600'],
    ['BATCH_REPORT_STATUS_UPDATED', 'bg-purple-500'],
    ['DOCUMENTS_MOVED_TO_PENDING', 'bg-yellow-400'],
    ['STALLED_RETRY_TRIGGERED', 'bg-orange-500'],
    ['PENDING_REPORT_CREATED', 'bg-yellow-500'],
    ['REPORT_PROCESSING', 'bg-gray-500'],
    ['REPORT_ERROR', 'bg-red-500'],
    ['REPORT_FAILED', 'bg-red-800'],
    ['REPORT_COMPLETED', 'bg-blue-500'],
]);

// --- Helpers ---

export const getEventColor = (eventName: string): string =>
    EVENT_COLOR_MAP.get(eventName) || 'bg-gray-300';

export const getCssColorByStatus = (status?: string): string => {
    if (!status) return 'bg-gray-800';
    return STATUS_MAP[status.toUpperCase()]?.bgClass || 'bg-gray-500';
};

export const getCssColorBy = (status?: string): string => {
    if (!status) return 'bg-yellow-500';
    return STATUS_MAP[status.toUpperCase()]?.bgClass || 'bg-yellow-500';
};

export const getStatusLabel = (status?: string): string => {
    if (!status) return '';
    return STATUS_MAP[status.toUpperCase()]?.label || status;
};

export const getContentByStatus = (status?: string, errorMessage?: string | null): string => {
    if (!status) return 'Consulta pendente';
    const cfg = STATUS_MAP[status.toUpperCase()];
    if (status === 'ERROR' && errorMessage) return `Erro: ${errorMessage}`;
    return cfg?.description || status;
};

// --- Export logic ---

export const exportPageToCsv = (rows: any[], headers: ATableHeader[], id: string, currentPage: number): void => {
    if (!rows.length || !headers.length) return;
    const sanitize = (val: string): string => /^[=+\-@\t\r]/.test(val) ? `'${val}` : val;
    const csvHeaders = headers.map(h => sanitize(h.title)).join(';');
    const csvRows = rows.map(row =>
        headers.map(h => {
            const val = (row as any)[h.field || h.id];
            if (val === null || val === undefined) return '';
            if (typeof val === 'object') return sanitize(JSON.stringify(val));
            return sanitize(String(val));
        }).join(';')
    ).join('\n');
    const csv = `${csvHeaders}\n${csvRows}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-report-${id}-page-${currentPage + 1}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};

export const exportFullBatch = async (fetcher: any, id: string): Promise<void> => {
    const response = await fetcher.get(`/batch-reports/${id}/download`);
    if (!response.ok) throw new Error('Download failed');
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        const json = await response.json();
        if (json.downloadUrl) {
            window.open(json.downloadUrl, '_blank');
            return;
        }
        throw new Error('Unexpected JSON response');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `batch-report-${id}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
};
